import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Truck, Store, CreditCard, MessageCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { useCart } from '@/context/CartContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cart, cartTotal, clearCart, cartItemCount } = useCart();
  const [settings, setSettings] = useState({ online_ordering_enabled: true, whatsapp_ordering_enabled: true, delivery_fee: 2.50, min_delivery_order: 10, delivery_radius_km: 4, pickup_discount_percentage: 10 });
  const [orderType, setOrderType] = useState('delivery');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [marketingOptIn, setMarketingOptIn] = useState(false);

  // Phone: strip anything that isn't a digit, space, +, -, ( or )
  const sanitizePhone = (raw) => raw.replace(/[^\d+\s\-()]/g, '');
  // Must have at least 9 digits (Spanish mobile is 9 digits)
  const digitsOnly = (customerPhone.match(/\d/g) || []).length;
  const phoneValid = digitsOnly >= 9;
  // Email is optional; if filled, it must be a plausible email
  const emailValid = customerEmail.trim() === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail.trim());
  const [notes, setNotes] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [whatsappPayMode, setWhatsappPayMode] = useState('card');
  const [cashAmount, setCashAmount] = useState('');
  const [redsysEnabled, setRedsysEnabled] = useState(false);
  const [zoneStatus, setZoneStatus] = useState({ state: 'idle', message: '' });
  // state: idle | checking | ok | error
  const [zoneChecking, setZoneChecking] = useState(false);

  const checkDeliveryZone = async () => {
    if (!customerAddress.trim()) {
      setZoneStatus({ state: 'idle', message: '' });
      return;
    }
    setZoneChecking(true);
    setZoneStatus({ state: 'checking', message: 'Verificando zona de reparto...' });
    try {
      const { data } = await axios.post(`${API}/delivery/check-zone`, { address: customerAddress });
      setZoneStatus({
        state: data.within_zone ? 'ok' : 'error',
        message: data.message,
        distance_km: data.distance_km,
      });
    } catch {
      setZoneStatus({ state: 'error', message: 'Error verificando zona. Intenta de nuevo.' });
    } finally {
      setZoneChecking(false);
    }
  };

  // Reset zone check when address changes
  useEffect(() => {
    if (zoneStatus.state !== 'idle') setZoneStatus({ state: 'idle', message: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerAddress]);

  useEffect(() => {
    if (cartItemCount === 0) {
      navigate('/');
      return;
    }
    axios.get(`${API}/settings`).then(r => setSettings(r.data)).catch(() => {});
    axios.get(`${API}/redsys/config`)
      .then(r => setRedsysEnabled(!!r.data?.enabled))
      .catch(() => setRedsysEnabled(false));
  }, [cartItemCount, navigate]);

  const estimatedTime = orderType === 'pickup' ? '20 min' : '50 min';
  const deliveryFee = orderType === 'delivery' ? (settings.delivery_fee || 2.50) : 0;
  const minDeliveryOrder = settings.min_delivery_order || 10;
  const belowMinimum = orderType === 'delivery' && cartTotal < minDeliveryOrder;
  const amountNeeded = Math.max(0, minDeliveryOrder - cartTotal);

  // ---------- Discount code ----------
  const [discountInput, setDiscountInput] = useState('');
  const [discount, setDiscount] = useState(null); // null | {valid, code, percentage, discount_amount, message}
  const [applying, setApplying] = useState(false);
  const applyDiscount = async () => {
    if (!discountInput.trim()) return;
    if (!customerPhone.trim() || !customerEmail.trim()) {
      toast.error('Introduce telefono y email antes de aplicar el codigo');
      return;
    }
    setApplying(true);
    try {
      const { data } = await axios.post(`${API}/discount/validate`, {
        code: discountInput,
        phone: customerPhone,
        email: customerEmail.trim(),
        subtotal: cartTotal,
        order_type: orderType,
      });
      if (data.valid) {
        setDiscount(data);
        toast.success(data.message);
      } else {
        setDiscount(null);
        toast.error(data.message);
      }
    } catch {
      toast.error('Error validando el codigo');
      setDiscount(null);
    } finally {
      setApplying(false);
    }
  };
  const clearDiscount = () => { setDiscount(null); setDiscountInput(''); };
  // Clear applied discount if key inputs change (since server re-checks eligibility)
  useEffect(() => { if (discount) setDiscount(null); /* eslint-disable-next-line */ }, [customerPhone, customerEmail, orderType, cartTotal]);

  const discountAmount = discount?.valid ? discount.discount_amount : 0;
  // Automatic pickup discount (no stacking with code: server uses max).
  const pickupPct = orderType === 'pickup' ? (settings.pickup_discount_percentage || 0) : 0;
  const pickupDiscountAmount = pickupPct > 0 ? Math.round(cartTotal * pickupPct) / 100 : 0;
  const appliedDiscountAmount = Math.max(discountAmount, pickupDiscountAmount);
  const appliedDiscountSource = appliedDiscountAmount === 0
    ? 'none'
    : (pickupDiscountAmount >= discountAmount ? 'pickup' : 'code');
  const orderTotal = cartTotal - appliedDiscountAmount + deliveryFee;

  // Better error handler
  const apiError = (error, fallback) => toast.error(error.response?.data?.detail || fallback);

  const handleRedsysPayment = async () => {
    if (!customerName || !customerPhone) {
      toast.error('Por favor, rellena nombre y telefono');
      return;
    }
    if (!phoneValid) {
      toast.error('Introduce un numero de telefono valido (minimo 9 cifras)');
      return;
    }
    if (!customerEmail.trim()) {
      toast.error('Email obligatorio para pagar online. Anade tu email o pulsa "Enviar Pedido por WhatsApp" debajo.');
      return;
    }
    if (!emailValid) {
      toast.error('Email no valido. Corrigelo para pagar online.');
      return;
    }
    if (orderType === 'delivery' && !customerAddress) {
      toast.error('Por favor, introduce tu direccion de entrega');
      return;
    }
    if (belowMinimum) {
      toast.error(`Pedido minimo para entrega: ${minDeliveryOrder.toFixed(2)} EUR. Te faltan ${amountNeeded.toFixed(2)} EUR.`);
      return;
    }
    setIsProcessing(true);
    try {
      const { data } = await axios.post(`${API}/redsys/create-session`, {
        cart_id: cart.id,
        origin_url: window.location.origin,
        customer_name: customerName,
        customer_phone: customerPhone,
        customer_email: customerEmail.trim(),
        customer_address: customerAddress,
        order_type: orderType,
        notes,
        discount_code: discount?.valid ? discount.code : '',
        language: 'es',
        marketing_opt_in: marketingOptIn,
      });
      // Build a hidden form and POST it to Redsys (browser leaves the SPA)
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = data.action;
      form.acceptCharset = 'UTF-8';
      Object.entries(data.fields).forEach(([name, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
    } catch (error) {
      apiError(error, 'Error al iniciar el pago con tarjeta');
      setIsProcessing(false);
    }
  };

  const handleWhatsAppOrder = async () => {
    if (!customerName || !customerPhone) {
      toast.error('Por favor, rellena nombre y telefono');
      return;
    }
    if (!phoneValid) {
      toast.error('Introduce un numero de telefono valido (minimo 9 cifras)');
      return;
    }
    if (!emailValid) {
      toast.error('Email no valido. Dejalo en blanco o corrigelo.');
      return;
    }
    if (orderType === 'delivery' && !customerAddress) {
      toast.error('Por favor, introduce tu direccion de entrega');
      return;
    }
    if (belowMinimum) {
      toast.error(`Pedido minimo para entrega: ${minDeliveryOrder.toFixed(2)} EUR. Te faltan ${amountNeeded.toFixed(2)} EUR.`);
      return;
    }
    if (whatsappPayMode === 'cash' && cashAmount && parseFloat(cashAmount) < orderTotal) {
      toast.error('El importe debe ser igual o mayor al total');
      return;
    }
    setIsProcessing(true);
    try {
      const changeInfo = whatsappPayMode === 'cash' 
        ? (cashAmount ? `Paga con ${parseFloat(cashAmount).toFixed(2)} EUR (cambio: ${(parseFloat(cashAmount) - orderTotal).toFixed(2)} EUR)` : 'Sin cambio necesario')
        : 'Pago con tarjeta al repartidor/recogida';
      
      const response = await axios.post(`${API}/orders/whatsapp`, {
        cart_id: cart.id,
        customer_name: customerName,
        customer_phone: customerPhone,
        customer_email: customerEmail.trim(),
        customer_address: customerAddress,
        order_type: orderType,
        notes: notes ? `${notes} | ${changeInfo}` : changeInfo,
        payment_mode: whatsappPayMode,
        cash_amount: whatsappPayMode === 'cash' ? parseFloat(cashAmount) || 0 : 0,
        discount_code: discount?.valid ? discount.code : '',
        marketing_opt_in: marketingOptIn,
      });
      const { message, whatsapp_number } = response.data;
      const encodedMessage = encodeURIComponent(message);
      const whatsappUrl = `https://wa.me/${whatsapp_number}?text=${encodedMessage}`;
      
      await clearCart();
      window.open(whatsappUrl, '_blank');
      navigate('/');
      toast.success('Pedido enviado por WhatsApp');
    } catch (error) {
      apiError(error, 'Error al crear el pedido');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="checkout-page">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-[#2D231E]/10">
        <div className="container-max flex items-center h-16">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-[#5C4D43] hover:text-[#C44536] transition-colors" data-testid="back-to-menu">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-sans text-sm font-medium">Volver al Menu</span>
          </button>
          <h1 className="font-serif text-xl font-bold text-[#2D231E] mx-auto">Finalizar Pedido</h1>
        </div>
      </header>

      <div className="container-max py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left: Customer Info */}
          <div className="space-y-6">
            {/* Order Type */}
            <div className="bg-[#F5EFE6] rounded-2xl p-6 border border-[#E8DEC8]">
              <h2 className="font-serif text-lg font-semibold text-[#2D231E] mb-4">Tipo de Pedido</h2>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setOrderType('pickup')}
                  className={`p-4 rounded-xl border-2 transition-all text-center ${
                    orderType === 'pickup' ? 'border-[#C44536] bg-[#C44536]/5' : 'border-[#E8DEC8] hover:border-[#C44536]/30'
                  }`}
                  data-testid="order-type-pickup"
                >
                  <Store className={`w-8 h-8 mx-auto mb-2 ${orderType === 'pickup' ? 'text-[#C44536]' : 'text-[#5C4D43]'}`} />
                  <p className="font-sans font-semibold text-[#2D231E]">Recogida</p>
                  <p className="font-sans text-xs text-[#5C4D43] mt-1">~20 min</p>
                </button>
                <button
                  onClick={() => setOrderType('delivery')}
                  className={`p-4 rounded-xl border-2 transition-all text-center ${
                    orderType === 'delivery' ? 'border-[#C44536] bg-[#C44536]/5' : 'border-[#E8DEC8] hover:border-[#C44536]/30'
                  }`}
                  data-testid="order-type-delivery"
                >
                  <Truck className={`w-8 h-8 mx-auto mb-2 ${orderType === 'delivery' ? 'text-[#C44536]' : 'text-[#5C4D43]'}`} />
                  <p className="font-sans font-semibold text-[#2D231E]">Entrega</p>
                  <p className="font-sans text-xs text-[#5C4D43] mt-1">~50 min</p>
                </button>
              </div>
            </div>

            {/* Customer Details */}
            <div className="bg-[#F5EFE6] rounded-2xl p-6 border border-[#E8DEC8]">
              <h2 className="font-serif text-lg font-semibold text-[#2D231E] mb-4">Datos del Cliente</h2>
              <div className="space-y-4">
                <div>
                  <label className="font-sans text-sm text-[#5C4D43] mb-1 block">Nombre *</label>
                  <Input
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Tu nombre completo"
                    className="bg-white border-[#E8DEC8] focus:ring-[#C44536] focus:border-transparent"
                    data-testid="customer-name-input"
                  />
                </div>
                <div>
                  <label className="font-sans text-sm text-[#5C4D43] mb-1 block">Telefono *</label>
                  <Input
                    type="tel"
                    inputMode="tel"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(sanitizePhone(e.target.value))}
                    placeholder="+34 600 000 000"
                    className="bg-white border-[#E8DEC8] focus:ring-[#C44536] focus:border-transparent"
                    data-testid="customer-phone-input"
                    aria-invalid={customerPhone && !phoneValid}
                  />
                  {customerPhone && !phoneValid && (
                    <p className="mt-1 text-xs text-[#C44536] font-sans" data-testid="phone-error">
                      Introduce un numero de telefono valido (minimo 9 cifras).
                    </p>
                  )}
                </div>
                <div>
                  <label className="font-sans text-sm text-[#5C4D43] mb-1 block">
                    Email <span className="text-[#8A7A6D]">(obligatorio para pagar con tarjeta)</span>
                  </label>
                  <Input
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    placeholder="tu@email.com"
                    className="bg-white border-[#E8DEC8] focus:ring-[#C44536] focus:border-transparent"
                    data-testid="customer-email-input"
                    aria-invalid={!emailValid}
                  />
                  {!emailValid && (
                    <p className="mt-1 text-xs text-[#C44536] font-sans" data-testid="email-error">
                      Email no valido. Corrigelo o dejalo en blanco y pide por WhatsApp.
                    </p>
                  )}
                  <label
                    className="mt-3 flex items-start gap-2 cursor-pointer select-none"
                    data-testid="marketing-consent-row"
                  >
                    <input
                      type="checkbox"
                      checked={marketingOptIn}
                      onChange={(e) => setMarketingOptIn(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-[#E8DEC8] accent-[#C44536] cursor-pointer"
                      data-testid="marketing-consent-checkbox"
                    />
                    <span className="font-sans text-xs text-[#5C4D43] leading-snug">
                      Quiero recibir ofertas, descuentos y novedades de Taj Mahal por email. Puedes darte de baja en cualquier momento.
                    </span>
                  </label>
                </div>
                {orderType === 'delivery' && (
                  <div>
                    <label className="font-sans text-sm text-[#5C4D43] mb-1 block">Direccion de entrega *</label>
                    <div className="flex gap-2">
                      <Input
                        value={customerAddress}
                        onChange={(e) => setCustomerAddress(e.target.value)}
                        placeholder="Calle, numero, piso..."
                        className="bg-white border-[#E8DEC8] focus:ring-[#C44536] focus:border-transparent flex-1"
                        data-testid="customer-address-input"
                      />
                      <Button
                        type="button"
                        onClick={checkDeliveryZone}
                        disabled={!customerAddress.trim() || zoneChecking}
                        variant="outline"
                        className="border-[#C44536] text-[#C44536] hover:bg-[#C44536]/5 whitespace-nowrap"
                        data-testid="check-zone-btn"
                      >
                        {zoneChecking ? (<><Loader2 className="w-4 h-4 animate-spin mr-1" />...</>) : 'Verificar zona'}
                      </Button>
                    </div>
                    {zoneStatus.state !== 'idle' && (
                      <p
                        className={
                          'mt-2 text-sm font-sans ' +
                          (zoneStatus.state === 'ok'
                            ? 'text-[#2C5E3E]'
                            : zoneStatus.state === 'error'
                              ? 'text-[#C44536]'
                              : 'text-[#5C4D43]')
                        }
                        data-testid="zone-status"
                      >
                        {zoneStatus.state === 'ok' ? '✓ ' : zoneStatus.state === 'error' ? '✗ ' : ''}
                        {zoneStatus.message}
                      </p>
                    )}
                    <p className="mt-1 text-xs text-[#8A7A6D] font-sans">
                      Solo repartimos en un radio de 4 km del restaurante.
                    </p>
                  </div>
                )}
                <div>
                  <label className="font-sans text-sm text-[#5C4D43] mb-1 block">Notas (opcional)</label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Alergias, instrucciones especiales..."
                    className="bg-white border-[#E8DEC8] focus:ring-[#C44536] focus:border-transparent"
                    rows={3}
                    data-testid="customer-notes-input"
                  />
                  <p className="mt-1.5 text-xs text-[#8A7A6D] font-sans" data-testid="allergen-notice">
                    <strong className="text-[#C44536]">Alergias o intolerancias?</strong> Indicalas aqui o llamanos al +34 602 704 745 antes de pedir. Nuestra cocina manipula gluten, lacteos, frutos secos, sesamo, soja, huevo, pescado y otros alergenos: no podemos garantizar la ausencia total de trazas. Consulta la{' '}
                    <a href="/alergenos" target="_blank" rel="noopener noreferrer" className="text-[#C44536] underline">
                      tabla de alergenos por plato
                    </a>{' '}
                    o nuestros{' '}
                    <a href="/terminos" target="_blank" rel="noopener noreferrer" className="text-[#C44536] underline">
                      Terminos
                    </a>.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Order Summary & Payment */}
          <div className="space-y-6">
            {/* Order Summary */}
            <div className="bg-[#F5EFE6] rounded-2xl p-6 border border-[#E8DEC8]">
              <h2 className="font-serif text-lg font-semibold text-[#2D231E] mb-4">Resumen del Pedido</h2>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {cart.items.map(item => (
                  <div key={item.menu_item_id} className="flex justify-between items-center" data-testid={`checkout-item-${item.menu_item_id}`}>
                    <div>
                      <span className="font-sans text-sm text-[#2D231E]">{item.name}</span>
                      <span className="font-sans text-xs text-[#5C4D43] ml-2">x{item.quantity}</span>
                      {item.spiciness && <span className="font-sans text-xs text-[#E28743] ml-2">({item.spiciness})</span>}
                    </div>
                    <span className="font-sans text-sm font-medium text-[#2D231E]">
                      {(item.price * item.quantity).toFixed(2)} EUR
                    </span>
                  </div>
                ))}
              </div>
              <Separator className="my-4 bg-[#E8DEC8]" />
              <div className="flex justify-between items-center mb-2">
                <span className="font-sans text-[#5C4D43]">Subtotal</span>
                <span className="font-sans text-[#2D231E]">{cartTotal.toFixed(2)} EUR</span>
              </div>
              {deliveryFee > 0 && (
                <div className="flex justify-between items-center mb-2">
                  <span className="font-sans text-[#5C4D43]">Envio a domicilio</span>
                  <span className="font-sans text-[#2D231E]">{deliveryFee.toFixed(2)} EUR</span>
                </div>
              )}
              {deliveryFee === 0 && (
                <div className="flex justify-between items-center mb-2">
                  <span className="font-sans text-[#5C4D43]">Recogida</span>
                  <span className="font-sans text-[#2C5E3E] font-medium">Gratis</span>
                </div>
              )}
              {appliedDiscountSource === 'pickup' && (
                <div className="flex justify-between items-center mb-2" data-testid="pickup-discount-line">
                  <span className="font-sans text-[#2C5E3E]">
                    Recogida -{pickupPct.toFixed(0)}%
                  </span>
                  <span className="font-sans text-[#2C5E3E] font-medium">
                    -{pickupDiscountAmount.toFixed(2)} EUR
                  </span>
                </div>
              )}
              {appliedDiscountSource === 'code' && discount?.valid && (
                <div className="flex justify-between items-center mb-2" data-testid="discount-line">
                  <span className="font-sans text-[#2C5E3E]">
                    Descuento ({discount.code} · -{discount.percentage.toFixed(0)}%)
                  </span>
                  <span className="font-sans text-[#2C5E3E] font-medium">
                    -{discount.discount_amount.toFixed(2)} EUR
                  </span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="font-serif text-lg font-semibold text-[#2D231E]">Total</span>
                <span className="font-serif text-2xl font-bold text-[#C44536]" data-testid="checkout-total">
                  {orderTotal.toFixed(2)} EUR
                </span>
              </div>

              {/* Discount code input */}
              <div className="mt-4 pt-4 border-t border-[#E8DEC8]">
                {!discount?.valid ? (
                  <div className="space-y-2">
                    <label className="font-sans text-sm text-[#5C4D43] block">Codigo de descuento</label>
                    <div className="flex gap-2">
                      <Input
                        value={discountInput}
                        onChange={(e) => setDiscountInput(e.target.value.toUpperCase())}
                        placeholder="Introduce tu codigo"
                        className="bg-white border-[#E8DEC8] uppercase"
                        maxLength={30}
                        data-testid="discount-code-input"
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyDiscount(); } }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={applyDiscount}
                        disabled={applying || !discountInput.trim()}
                        className="border-[#C44536] text-[#C44536] hover:bg-[#C44536]/5 whitespace-nowrap"
                        data-testid="apply-discount-btn"
                      >
                        {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aplicar'}
                      </Button>
                    </div>
                  </div>
                ) : appliedDiscountSource === 'code' ? (
                  <div className="flex items-center justify-between bg-[#2C5E3E]/10 rounded-lg px-3 py-2" data-testid="discount-applied">
                    <span className="font-sans text-sm text-[#2C5E3E]">
                      ✓ Codigo <strong>{discount.code}</strong> aplicado
                    </span>
                    <button
                      type="button"
                      onClick={clearDiscount}
                      className="text-[#5C4D43] hover:text-[#C44536] text-sm underline"
                      data-testid="remove-discount"
                    >
                      Quitar
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between bg-[#E28743]/10 rounded-lg px-3 py-2" data-testid="discount-superseded">
                    <span className="font-sans text-xs text-[#5C4D43]">
                      Tu codigo <strong>{discount.code}</strong> da -{discount.percentage.toFixed(0)}%, pero el descuento de Recogida (-{pickupPct.toFixed(0)}%) es mejor y ya esta aplicado.
                    </span>
                    <button
                      type="button"
                      onClick={clearDiscount}
                      className="text-[#5C4D43] hover:text-[#C44536] text-sm underline ml-2 whitespace-nowrap"
                      data-testid="remove-discount"
                    >
                      Quitar
                    </button>
                  </div>
                )}
              </div>
              <div className="mt-3 p-3 bg-[#2C5E3E]/10 rounded-lg">
                <p className="font-sans text-sm text-[#2C5E3E] font-medium">
                  Tiempo estimado: {estimatedTime}
                </p>
              </div>
              {belowMinimum && (
                <div className="mt-3 p-3 bg-[#C44536]/10 border border-[#C44536]/30 rounded-lg" data-testid="min-order-warning">
                  <p className="font-sans text-sm text-[#C44536] font-semibold">
                    Pedido minimo para entrega: {minDeliveryOrder.toFixed(2)} EUR
                  </p>
                  <p className="font-sans text-xs text-[#C44536] mt-0.5">
                    Te faltan {amountNeeded.toFixed(2)} EUR. Anade mas articulos o elige Recogida (sin minimo).
                  </p>
                </div>
              )}
            </div>

            {/* Payment Methods */}
            <div className="space-y-4">
              {settings.online_ordering_enabled && redsysEnabled && (
                <Button
                  onClick={handleRedsysPayment}
                  disabled={isProcessing || belowMinimum}
                  className="btn-primary w-full py-6 text-lg flex items-center justify-center gap-3"
                  data-testid="pay-redsys-btn"
                >
                  {isProcessing ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <CreditCard className="w-5 h-5" />
                  )}
                  Pagar con tarjeta - {orderTotal.toFixed(2)} EUR
                </Button>
              )}

              {settings.whatsapp_ordering_enabled && (
                <div className="bg-[#F5EFE6] rounded-2xl p-5 border border-[#E8DEC8] space-y-4">
                  <h3 className="font-serif text-base font-semibold text-[#2D231E] flex items-center gap-2">
                    <MessageCircle className="w-5 h-5 text-[#25D366]" />
                    Pedir por WhatsApp
                  </h3>
                  
                  {/* Payment Mode Selection */}
                  <div>
                    <p className="font-sans text-sm text-[#5C4D43] mb-3">Metodo de pago al recibir:</p>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => { setWhatsappPayMode('card'); setCashAmount(''); }}
                        className={`p-3 rounded-xl border-2 transition-all text-center ${
                          whatsappPayMode === 'card' ? 'border-[#C44536] bg-[#C44536]/5' : 'border-[#E8DEC8] hover:border-[#C44536]/30'
                        }`}
                        data-testid="whatsapp-pay-card"
                      >
                        <CreditCard className={`w-6 h-6 mx-auto mb-1 ${whatsappPayMode === 'card' ? 'text-[#C44536]' : 'text-[#5C4D43]'}`} />
                        <p className="font-sans text-sm font-medium text-[#2D231E]">Tarjeta</p>
                      </button>
                      <button
                        onClick={() => setWhatsappPayMode('cash')}
                        className={`p-3 rounded-xl border-2 transition-all text-center ${
                          whatsappPayMode === 'cash' ? 'border-[#C44536] bg-[#C44536]/5' : 'border-[#E8DEC8] hover:border-[#C44536]/30'
                        }`}
                        data-testid="whatsapp-pay-cash"
                      >
                        <span className={`text-2xl block mb-1 ${whatsappPayMode === 'cash' ? 'text-[#C44536]' : 'text-[#5C4D43]'}`}>&#128181;</span>
                        <p className="font-sans text-sm font-medium text-[#2D231E]">Efectivo</p>
                      </button>
                    </div>
                  </div>

                  {/* Cash Change Input */}
                  {whatsappPayMode === 'cash' && (
                    <div className="space-y-2">
                      <label className="font-sans text-sm text-[#5C4D43]">
                        Pagara con: (Total: {orderTotal.toFixed(2)} EUR)
                      </label>
                      <div className="flex gap-2 flex-wrap">
                        {[20, 50, 100].filter(v => v >= orderTotal).map(val => (
                          <button
                            key={val}
                            onClick={() => setCashAmount(String(val))}
                            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                              cashAmount === String(val) ? 'bg-[#C44536] text-white' : 'bg-white border border-[#E8DEC8] text-[#2D231E] hover:border-[#C44536]'
                            }`}
                            data-testid={`cash-${val}`}
                          >
                            {val} EUR
                          </button>
                        ))}
                        <button
                          onClick={() => setCashAmount('')}
                          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                            cashAmount === '' ? 'bg-[#C44536] text-white' : 'bg-white border border-[#E8DEC8] text-[#2D231E] hover:border-[#C44536]'
                          }`}
                          data-testid="cash-exact"
                        >
                          Importe exacto
                        </button>
                      </div>
                      <Input
                        type="number"
                        value={cashAmount}
                        onChange={(e) => setCashAmount(e.target.value)}
                        placeholder="Otro importe..."
                        className="bg-white border-[#E8DEC8] mt-1"
                        data-testid="cash-custom-amount"
                      />
                      {cashAmount && parseFloat(cashAmount) >= orderTotal && (
                        <p className="font-sans text-sm text-[#2C5E3E] font-medium">
                          Cambio: {(parseFloat(cashAmount) - orderTotal).toFixed(2)} EUR
                        </p>
                      )}
                    </div>
                  )}

                  <Button
                    onClick={handleWhatsAppOrder}
                    disabled={isProcessing || belowMinimum}
                    className="w-full py-5 text-base flex items-center justify-center gap-3 rounded-full bg-[#25D366] text-white hover:bg-[#1DA851] transition-all hover:-translate-y-0.5 shadow-sm"
                    data-testid="pay-whatsapp-btn"
                  >
                    {isProcessing ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <MessageCircle className="w-5 h-5" />
                    )}
                    Enviar Pedido por WhatsApp
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
